import { LABELS } from '@/constants/common/message.constants'
import { type CustomKeySettingEntity } from '@/libs/api'

/**
 * `ShipmentOrder.customKey1`〜`customKey5` に対応するフォーム/DTOのフィールド名（#3463 VOC_PJ-829/851）。
 */
export type CustomKeyFieldName =
  | 'customKey1'
  | 'customKey2'
  | 'customKey3'
  | 'customKey4'
  | 'customKey5'

/**
 * 配送依頼ドロワー検索の複数値OR検索フィールド名（`customKey1s`〜`customKey5s`）。
 * `ShipmentOrdersListSearchOptionDto` / 検索フォームスキーマのフィールド名と一致させる。
 */
export type CustomKeySearchFieldName = `${CustomKeyFieldName}s`

/**
 * 登録・編集・検索画面に表示する1スロット分のカスタムキー情報。
 */
export type VisibleCustomKeySlot = {
  slotNumber: number
  fieldName: CustomKeyFieldName
  /** 配送依頼ドロワー検索用のフィールド名（`customKey1s` 等、複数値OR検索） */
  searchFieldName: CustomKeySearchFieldName
  /** マスタで設定されたラベル。未設定時は「カスタムキーN」にフォールバックする */
  label: string
}

/**
 * `CustomKeySetting` の一覧から、フォーム/検索に表示すべきスロット（`isVisible === true`）のみを
 * `slotNumber` 昇順で抽出する純関数（#3463 D3: 固定Union型を廃した動的表示リストの共通ロジック）。
 */
export const getVisibleCustomKeySlots = (
  settings: CustomKeySettingEntity[] | undefined
): VisibleCustomKeySlot[] => {
  if (!settings) return []
  return settings
    .filter((setting) => setting.isVisible)
    .sort((a, b) => a.slotNumber - b.slotNumber)
    .map((setting) => {
      const fieldName = `customKey${setting.slotNumber}` as CustomKeyFieldName
      return {
        slotNumber: setting.slotNumber,
        fieldName,
        searchFieldName: `${fieldName}s` as CustomKeySearchFieldName,
        label:
          setting.label || LABELS.CUSTOM_KEY_FALLBACK_LABEL(setting.slotNumber)
      }
    })
}

/**
 * `CustomKeySetting` の一覧から、矢羽根に表示すべきスロット（`showValue === true`）を
 * `slotNumber` 昇順で1件だけ解決する純関数（#4315 VOC_PJ-937）。
 * マスタ画面側で相互排他を担保しているため通常は0/1件だが、複数タブ同時保存等でDB上に
 * 複数`true`が残っていても`slotNumber`最小の1件を防御的に採用する。
 */
export const resolveShowValueCustomKeySlot = (
  settings: CustomKeySettingEntity[] | undefined
): VisibleCustomKeySlot | undefined => {
  if (!settings) return undefined
  const [slot] = settings
    .filter((setting) => setting.showValue)
    .sort((a, b) => a.slotNumber - b.slotNumber)
    .map((setting) => {
      const fieldName = `customKey${setting.slotNumber}` as CustomKeyFieldName
      return {
        slotNumber: setting.slotNumber,
        fieldName,
        searchFieldName: `${fieldName}s` as CustomKeySearchFieldName,
        label:
          setting.label || LABELS.CUSTOM_KEY_FALLBACK_LABEL(setting.slotNumber)
      }
    })
  return slot
}
